// ============================================================
// LIFF (LINE Front-end Framework) 認証
// ============================================================
// LIFF はサブ動線(Tall サイズ・LINE トーク内の「ちょい見」と認証ゲート)
// メイン UI は PWA(link-board.jp)・全機能はそちらで提供
// 詳細: docs/CLAUDE_ARCHITECTURE.md「LIFF と PWA の役割分担」
//
// 原則0.5: 認証は LIFF 経由のみ(メール認証廃止)
// ============================================================

import liff from '@line/liff';
import { supabase } from './supabase';

const LIFF_ID = import.meta.env.VITE_LIFF_ID;

let initialized = false;

export async function initLiff(): Promise<void> {
  if (initialized) return;
  if (!LIFF_ID) {
    throw new Error('VITE_LIFF_ID is not set');
  }
  await liff.init({ liffId: LIFF_ID });
  initialized = true;
}

export async function ensureLiffLogin(): Promise<void> {
  await initLiff();
  if (!liff.isLoggedIn()) {
    liff.login({ redirectUri: window.location.href });
    // login() でリダイレクトされるため return しない
    await new Promise(() => {}); // 永久 pending
  }
}

export async function getLiffProfile() {
  await initLiff();
  if (!liff.isLoggedIn()) throw new Error('Not logged in to LIFF');
  return await liff.getProfile();
}

export async function getLiffIdToken(): Promise<string | null> {
  await initLiff();
  return liff.getIDToken();
}

/**
 * LIFF の ID トークンを Supabase の signInWithIdToken に渡して認証
 * 失敗した場合は、Edge Function 経由で tenant を upsert する fallback パスへ
 */
export async function signInWithLine(): Promise<{ tenantId: string | null }> {
  await ensureLiffLogin();
  const idToken = await getLiffIdToken();
  if (!idToken) throw new Error('Failed to get LIFF ID token');

  // パターンA: Supabase の line provider が有効な場合
  // const { data, error } = await supabase.auth.signInWithIdToken({
  //   provider: 'line',
  //   token: idToken,
  // });

  // パターンB: 自前 Edge Function 経由で tenant を確保 + JWT を発行
  const profile = await getLiffProfile();
  const functionsUrl = import.meta.env.VITE_FUNCTIONS_URL
    ?? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
  const res = await fetch(`${functionsUrl}/auth-line`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      id_token: idToken,
      line_user_id: profile.userId,
      display_name: profile.displayName,
    }),
  });
  if (!res.ok) {
    // 初回登録(オンボーディング)へ
    return { tenantId: null };
  }
  // 新方式(2026-05-13): auth-line は magic-link の token_hash を返す。
  // client が verifyOtp({ type:'magiclink', token_hash }) でセッション確立。
  // ES256 非対称 JWT に切り替わった Supabase で自前 HS256 署名が使えなくなったための代替実装。
  // 注意:Supabase は admin.generateLink({type:'magiclink'}) を内部的に recovery_token として
  // 保存する(auth.one_time_tokens.token_type='recovery_token')が、verifyOtp 側で
  // type='magiclink' を指定すれば内部マッピングで正しく照合される(動作確認済)。
  const json = (await res.json()) as { tenant_id: string; email: string; token_hash: string };
  const { error } = await supabase.auth.verifyOtp({
    type: 'magiclink',
    token_hash: json.token_hash,
  });
  if (error) {
    console.error('verifyOtp failed', error);
    throw new Error(`auth session failed: ${error.message}`);
  }
  return { tenantId: json.tenant_id };
}

export { liff };
